import { useState, useEffect, useCallback, Fragment } from 'react';
import { useSelector } from 'react-redux';
import OverallCompletion from '../components/OverallCompletion';
import CompletionBreakdown from '../components/CompletionBreakdown';
import ExpandableGeneric from '../components/ExpandableGeneric';
import ExpandableGenericDynamic from '../components/ExpandableGeneric/ExpandableGenericDynamic';
import StandardsDashboard from '../components/StandardsDashboard';
import {
  getSecondaryCompletion,
  getSupplementaryCompletion,
  getUnitCompletion,
  getAllApplicableStandards,
  getTotalCompletedStandards,
} from './completionHelpers';
import { getMiddleSchoolUnits, getHighSchoolUnits } from '../components/LibraryCard/cardData';
import { getELAStandardsMap } from '../BackendInterface';
import { selectClassroom } from '../Classrooms';
import { getUsersEnableContentTags } from '../GlobalFunctions';
import { RootState } from '../ApplicationState';
import styles from './CurriculumMiddleSchoolDashboard.module.scss';
import { ELAStandardsMap } from '@peekapak/types/LessonPlanTypes';

const CurriculumMiddleSchoolDashboard = (): JSX.Element => {
  const [isCurriculumExpanded, setIsCurriculumExpanded] = useState(false);
  const [isStandardsExpanded, setIsStandardsExpanded] = useState(false);
  const [isSupplementaryExpanded, setIsSupplementaryExpanded] = useState(false);
  const [standardsMap, setStandardsMap] = useState<ELAStandardsMap>();
  const [isLoaded, setIsLoaded] = useState(false);
  const [_resizeTrigger, triggerResize] = useState(false);
  const classroom = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom;
  });
  const completionStatus = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom?.completionStatus;
  });

  const grade = useSelector((state: RootState) => {
    const selectedClassroom = selectClassroom(state);
    return selectedClassroom?.grade;
  });

  const expandUnits = () => {
    setIsCurriculumExpanded((prevState) => !prevState);
  };
  const expandStandards = () => {
    setIsStandardsExpanded((prevState) => !prevState);
  };
  const expandSupplementary = () => {
    setIsSupplementaryExpanded((prevState) => !prevState);
  };
  const handleResize = () => {
    triggerResize((prevState) => !prevState);
  };

  const enabledContentTags = getUsersEnableContentTags();
  const isMenaUser = enabledContentTags && enabledContentTags.includes('mena');

  const secondaryCompletion = completionStatus && getSecondaryCompletion(completionStatus, grade);

  // Get ALL curriculum units (non-supplementary) for the user's grade, filtered by region
  const allUnits = [...getMiddleSchoolUnits(), ...getHighSchoolUnits()];
  const curriculumUnits = allUnits.filter((unit) => {
    const isMenaContent = unit.applicationTags?.includes('mena');
    if (isMenaContent && !isMenaUser) return false;
    return unit.gradeSpan.includes(grade) && !unit.filterTags?.includes('supplementary');
  });

  // Total lessons = sum of numModules for ALL curriculum units (use standardsMap when available for consistency with per-unit display)
  const totalCurriculumModules = curriculumUnits.reduce((acc, unit) => {
    if (standardsMap?.data) {
      const unitData = standardsMap.data[unit.unit];
      if (unitData) {
        return acc + Object.keys(unitData).length;
      }
    }
    return acc + (unit.numModules || 0);
  }, 0);

  // Count complete lessons across ALL curriculum units (including unstarted ones)
  const totalCompleteModules = curriculumUnits.reduce((acc, unit) => {
    const unitId = unit.unit;
    // Check for MENA variants in completion data
    const unitCompletion =
      secondaryCompletion?.[unitId] ||
      secondaryCompletion?.[`${unitId}-mena`] ||
      (unitId.includes('-mena') ? secondaryCompletion?.[unitId.replace('-mena', '')] : null);
    if (!unitCompletion) return acc;
    const completeLessons = Object.values(unitCompletion).filter((lessonCompletion) => {
      const moduleStatuses = Object.values(lessonCompletion);
      return moduleStatuses.length > 0 && moduleStatuses.every((v) => v === 'Completed' || v === 'DeemedCompleted');
    }).length;
    return acc + completeLessons;
  }, 0);

  const secondaryUnitCompletion = secondaryCompletion && getUnitCompletion(secondaryCompletion, grade, standardsMap);

  const supplementaryCompletion = completionStatus && getSupplementaryCompletion(completionStatus, grade);

  // Get ALL supplementary units for the grade (filtered by region) to always render the section
  const allUnitsForSupp = [...getMiddleSchoolUnits(), ...getHighSchoolUnits()];
  const validSupplementaryUnits = allUnitsForSupp.filter((unit) => {
    const isMenaContent = unit.applicationTags?.includes('mena');
    if (isMenaContent && !isMenaUser) return false;
    return unit.filterTags?.includes('supplementary') && unit.gradeSpan.includes(grade);
  });

  const supplementaryUnitCompletion = getUnitCompletion(supplementaryCompletion || {}, grade, standardsMap, true);
  const totalCompleteSupplementaryModules = supplementaryUnitCompletion.reduce(
    (acc, unit) => acc + unit.completeModules,
    0,
  );
  const totalSupplementaryModules = supplementaryUnitCompletion.reduce((acc, unit) => acc + unit.totalModules, 0);

  const allApplicableStandards = standardsMap && getAllApplicableStandards(standardsMap, grade);

  const uniqueStandards = allApplicableStandards && [...new Set(allApplicableStandards)];

  const totalStandards = uniqueStandards?.length || 1;

  // Combine curriculum and supplementary completion for full standards completion count
  const combinedCompletion = { ...secondaryCompletion, ...supplementaryCompletion };
  const totalCompleteStandards =
    combinedCompletion && standardsMap && getTotalCompletedStandards(combinedCompletion, standardsMap, grade);

  const getStandardsData = useCallback(async () => {
    const standardsFromServer = await getELAStandardsMap(grade);
    setStandardsMap(standardsFromServer);
  }, [grade]);

  useEffect(() => {
    if (completionStatus) {
      setIsLoaded(true);
    }
    if (grade) {
      getStandardsData();
    }
  }, [completionStatus, grade, getStandardsData]);

  return (
    <div className={styles.container}>
      <div className={styles.titleBar}>
        <h2 className={styles.reportTitle}>Curriculum Completion</h2>
      </div>
      <div className={styles.dataContainer}>
        {!isLoaded ? null : (!secondaryCompletion || Object.keys(secondaryCompletion).length === 0) &&
          (!supplementaryCompletion || Object.keys(supplementaryCompletion).length === 0) &&
          validSupplementaryUnits.length === 0 ? (
          <div className={styles.noData}>
            This class does not have any completion data. Report will be generated as lessons are completed.
          </div>
        ) : (
          <Fragment>
            {(secondaryCompletion && Object.keys(secondaryCompletion).length > 0) ||
            (supplementaryCompletion && Object.keys(supplementaryCompletion).length > 0) ? (
              <>
                <OverallCompletion
                  title='Curriculum'
                  handleClick={expandUnits}
                  numComplete={totalCompleteModules || 0}
                  totalNum={totalCurriculumModules}
                  textButton={`${isCurriculumExpanded ? 'Hide' : 'Show'} Unit Detail`}
                  barColor='#012a73'
                  isExpanded={isCurriculumExpanded}
                />

                <ExpandableGeneric isExpanded={isCurriculumExpanded}>
                  {secondaryUnitCompletion && (
                    <CompletionBreakdown
                      colA='Unit Name'
                      colB='Unit Progress'
                      colC='Completion'
                      data={secondaryUnitCompletion}
                      barColor='#012a73'
                      isShouldAnimate={isCurriculumExpanded}
                    />
                  )}
                </ExpandableGeneric>
              </>
            ) : null}
            {validSupplementaryUnits.length > 0 ? (
              <>
                <OverallCompletion
                  title='Supplementary Units'
                  handleClick={expandSupplementary}
                  numComplete={totalCompleteSupplementaryModules || 0}
                  totalNum={totalSupplementaryModules}
                  textButton={`${isSupplementaryExpanded ? 'Hide' : 'Show'} Unit Detail`}
                  barColor='#012a73'
                  isExpanded={isSupplementaryExpanded}
                />

                <ExpandableGeneric isExpanded={isSupplementaryExpanded}>
                  {supplementaryUnitCompletion && (
                    <CompletionBreakdown
                      colA='Unit Name'
                      colB='Unit Progress'
                      colC='Completion'
                      data={supplementaryUnitCompletion}
                      barColor='#012a73'
                      isShouldAnimate={isSupplementaryExpanded}
                    />
                  )}
                </ExpandableGeneric>
              </>
            ) : null}
            <OverallCompletion
              numComplete={totalCompleteStandards || 0}
              totalNum={totalStandards}
              title='ELA Standards'
              handleClick={expandStandards}
              textButton={`${isStandardsExpanded ? 'Hide' : 'Show'} Detail`}
              barColor='#012a73'
              isExpanded={isStandardsExpanded}
            />
            <ExpandableGenericDynamic isExpanded={isStandardsExpanded}>
              {standardsMap && combinedCompletion && classroom && (
                <StandardsDashboard
                  standardsMap={standardsMap}
                  completionStatus={combinedCompletion}
                  handleResize={handleResize}
                  classroom={classroom}
                  grade={grade}
                />
              )}
            </ExpandableGenericDynamic>
          </Fragment>
        )}
      </div>
    </div>
  );
};

export default CurriculumMiddleSchoolDashboard;
